/**
 * The straight 30/70 tunnel of the weekly check-up's small charts (#6627). Pure, so a unit test
 * can drive it (`trend-chart-tunnel.test.ts`).
 *
 * The chart's own median line is untouched: it stays the straight 14-day fit the weekly verdict
 * is read from. The tunnel is fitted on the same values with the same day numbering (days back
 * from the last value, the last value being day 0), by `straightTrend` in
 * `shared/src/body-comp-overview.ts`, and read only at the first and last day SHOWN. Its two
 * lines are the 30th and the 70th percentile slope, each through its own median intercept. They
 * cross near the middle of the 14 days, so over the 7 days shown the tunnel starts narrow and
 * widens to the right; that wedge is correct. No smoothing and no direction colours: this chart
 * must not read as a second verdict.
 */

import { straightTrend, TrendSeries } from '@cf-shared/body-comp-overview';

/** fewest readings, on different days, the tunnel needs */
export const MIN_TUNNEL_READINGS = 3;

/** the tunnel's lower and upper edge at the first and the last shown day */
export interface TrendChartTunnel {
  start: { low: number; high: number };
  end: { low: number; high: number };
}

/**
 * The straight tunnel over the last `shownDays` of `values` (one value per day, oldest first;
 * a missing day is undefined or NaN), or undefined with fewer than 3 readings.
 */
export function straightTunnelForShownDays(values: readonly (number | undefined | null)[], shownDays: number): TrendChartTunnel | undefined {
  const points: { x: number; y: number }[] = [];
  values.forEach((value, index) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      points.push({ x: index - (values.length - 1), y: value });
    }
  });
  if (points.length < MIN_TUNNEL_READINGS || shownDays < 1) return undefined;
  // this chart draws whatever it is given, as its median line always has, so the minimum-data
  // rule of the plan check is not applied again; the series kind then does not matter
  const fit = straightTrend(points, { firstDay: -(shownDays - 1), lastDay: 0, series: TrendSeries.BodyFat, alreadyJudgedEnough: true });
  if (!fit) return undefined;
  return { start: { low: fit.start.low, high: fit.start.high }, end: { low: fit.end.low, high: fit.end.high } };
}
